import { useCallback, useEffect, useState } from 'react';
import { readRaw, writeRaw } from '../lib/storage.ts';

export type ThemeMode = 'system' | 'dark' | 'light';

const KEY = 'theme';
const MQ = '(prefers-color-scheme: dark)';

function systemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MQ).matches;
}

function resolve(mode: ThemeMode): 'dark' | 'light' {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return systemDark() ? 'dark' : 'light';
}

function readMode(): ThemeMode {
  const raw = readRaw(KEY);
  return raw === 'dark' || raw === 'light' || raw === 'system' ? raw : 'system';
}

/** Theme preference: system (default, follows prefers-color-scheme) / dark /
 *  light. Persists to localStorage and applies data-theme on <html>. */
export function useTheme(): {
  mode: ThemeMode;
  resolved: 'dark' | 'light';
  setTheme: (m: ThemeMode) => void;
} {
  const [mode, setMode] = useState<ThemeMode>(readMode);
  const [resolved, setResolved] = useState<'dark' | 'light'>(() => resolve(readMode()));

  useEffect(() => {
    const applyNow = () => {
      const r = resolve(mode);
      setResolved(r);
      document.documentElement.setAttribute('data-theme', r);
    };
    applyNow();
    if (mode !== 'system') return;
    const mq = window.matchMedia(MQ);
    mq.addEventListener('change', applyNow);
    return () => mq.removeEventListener('change', applyNow);
  }, [mode]);

  const setTheme = useCallback((next: ThemeMode) => {
    setMode(next);
    writeRaw(KEY, next);
  }, []);

  return { mode, resolved, setTheme };
}
